import { useMemo } from 'react';

import type { IconV2Props } from '@withjoy/joykit';
import { HolidayCard, Invitation, MenuCard, Napkin, NewNote, PrintCard, SaveTheDate, Shipping, ThankYou, WelcomeSign } from '@withjoy/joykit/icons';
import { useDashboardTelemetry } from '@apps/admin/routes/Dashboard/Dashboard.telemetry';
import { useRouterHelper, useTranslation } from '@shared/core';
import { useStationeryEntitiesCount } from '@apps/card/components/CardsTabNav/useStationeryEntitiesCount';
import type { ProductRowSubItem } from '../../PrimaryDashboardContent/ProductRowItem';

export type StationeryMenuItem = Readonly<{
  id: string;
  title: string;
  path: string;
  pathIsInJoyWeb: true;
  onClick: (categoryArg?: string, fromDropdown?: boolean) => void;
  hasDivider?: boolean;
  isConditional?: 'drafts' | 'orders';
  icon?: React.ComponentType<IconV2Props>;
}>;

export type StationeryMenuResult = Readonly<{
  items: readonly StationeryMenuItem[];
  draftsCount: number;
  ordersCount: number;
}>;

/**
 * Telemetry section value used for every Stationery dropdown event (parent tile + children).
 * Carried over verbatim from the pre-refactor `dayOfStationery` parent tile, where children
 * were copy-pasted from the messaging tile and the parent matched. Don't change without
 * coordinating with the analytics consumers.
 */
export const STATIONERY_TELEMETRY_SECTION = 'Messaging';

/**
 * Source-of-truth hook for the Admin "Stationery" menu.
 *
 * Consumed by:
 * - Dashboard V2 Stationery tile dropdown (synthetic `stationery-dropdown` item)
 * - Admin header bar hamburger menu (via useProductRows flattening)
 *
 * The CategoryNav in the Cards app mirrors the ordering of categories shown here
 * but does not consume this hook at runtime — see
 * src/apps/card/routes/Dashboard/components/CategoryNav/useNavItems.ts.
 */
export const useStationeryMenu = (): StationeryMenuResult => {
  const routeHelpers = useRouterHelper();
  const telemetry = useDashboardTelemetry();
  const { t2, tq } = useTranslation('adminDashboardV2');
  const { draftsCount, ordersCount } = useStationeryEntitiesCount();

  return useMemo<StationeryMenuResult>(() => {
    const { invites: invitesStrings } = t2('sections');
    const { children: invitesChildren } = invitesStrings;

    const openPage =
      (label: string): StationeryMenuItem['onClick'] =>
      (categoryArg, fromDropdown) => {
        telemetry.openPage({ label, section: fromDropdown ? STATIONERY_TELEMETRY_SECTION : '', categoryArg });
      };

    const hasConditionalHeader = draftsCount > 0 || ordersCount > 0;

    const conditionalItems: StationeryMenuItem[] = [];

    if (draftsCount > 0) {
      conditionalItems.push({
        id: 'stationery-drafts',
        title: tq`Drafts`,
        path: routeHelpers.buildPath('/cards/drafts'),
        pathIsInJoyWeb: true,
        onClick: openPage('stationery.drafts'),
        isConditional: 'drafts',
        icon: NewNote,
      });
    }

    if (ordersCount > 0) {
      conditionalItems.push({
        id: 'stationery-orders',
        title: tq`Orders`,
        path: routeHelpers.buildPath('/cards/orders'),
        pathIsInJoyWeb: true,
        onClick: openPage('stationery.orders'),
        isConditional: 'orders',
        icon: Shipping,
      });
    }

    const staticItems: StationeryMenuItem[] = [
      {
        id: 'stationery-saveTheDate',
        title: invitesChildren.saveTheDates.title,
        path: routeHelpers.buildPath('/cards/wedding/save-the-date'),
        pathIsInJoyWeb: true,
        onClick: openPage('saveTheDates.paper'),
        hasDivider: hasConditionalHeader,
        icon: SaveTheDate,
      },
      {
        id: 'stationery-invitation',
        title: invitesChildren.invitations.title,
        path: routeHelpers.buildPath('/cards/wedding/invitation'),
        pathIsInJoyWeb: true,
        onClick: openPage('invitations.paper'),
        icon: Invitation,
      },
      {
        id: 'stationery-thankYou',
        title: invitesChildren.thankYou.title,
        path: routeHelpers.buildPath('/cards/wedding/thank-you'),
        pathIsInJoyWeb: true,
        onClick: openPage('invitations.thankYou'),
        icon: ThankYou,
      },
      {
        id: 'stationery-signs',
        title: invitesChildren.dayOfStationery.children.signs.title,
        path: routeHelpers.buildPath('/cards/wedding/sign'),
        pathIsInJoyWeb: true,
        onClick: openPage('dayOfStationery.signs'),
        icon: WelcomeSign,
      },
      {
        id: 'stationery-menus',
        title: invitesChildren.dayOfStationery.children.menus.title,
        path: routeHelpers.buildPath('/cards/wedding/menu'),
        pathIsInJoyWeb: true,
        onClick: openPage('dayOfStationery.menus'),
        icon: MenuCard,
      },
      {
        id: 'stationery-napkins',
        title: invitesChildren.dayOfStationery.children.napkins.title,
        path: routeHelpers.buildPath('/cards/wedding/napkin'),
        pathIsInJoyWeb: true,
        onClick: openPage('dayOfStationery.napkins'),
        icon: Napkin,
      },
      {
        id: 'stationery-enclosure',
        title: tq`Enclosure Cards`,
        path: routeHelpers.buildPath('/cards/wedding/enclosure'),
        pathIsInJoyWeb: true,
        onClick: openPage('stationery.enclosure'),
        icon: PrintCard,
      },
      {
        id: 'stationery-holiday',
        title: tq`Holiday Cards`,
        path: routeHelpers.buildPath('/cards/wedding/holiday'),
        pathIsInJoyWeb: true,
        onClick: openPage('stationery.holiday'),
        icon: HolidayCard,
      },
    ];

    return {
      items: [...conditionalItems, ...staticItems],
      draftsCount,
      ordersCount,
    };
    // telemetry is intentionally excluded — it's only captured in onClick closures where staleness is acceptable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftsCount, ordersCount, routeHelpers, t2, tq]);
};

/**
 * Project a `StationeryMenuItem` into the `ProductRowSubItem` shape expected by the
 * dashboard tile dropdown. Drops `id` and `isConditional` (not part of the sub-item
 * contract). Centralized so schema growth stays in one place.
 */
export const toProductRowSubItem = (item: StationeryMenuItem): ProductRowSubItem => ({
  title: item.title,
  path: item.path,
  pathIsInJoyWeb: item.pathIsInJoyWeb,
  onClick: item.onClick,
  hasDivider: item.hasDivider,
  icon: item.icon,
});
